import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

export const ProtectedRoute = ({ children, adminOnly = false, mentorOnly = false, blogsAdminOnly = false, siteContentOnly = false, superAdminOnly = false }) => {
  const { user, loading, isAdmin, isMentor, isSEO, canManageBlogs, canManageSiteContent, isSuperAdmin } = useAuth();
  const location = useLocation();
  
  // Check for incoming SSO auth_token
  const params = new URLSearchParams(window.location.search);
  const incomingToken = params.get('auth_token');
  const storedToken = localStorage.getItem('elevate360_token');
  
  // If there's an incoming SSO token, allow access (App.js will handle storage)
  if (incomingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-[#0D1B2A] rounded-xl flex items-center justify-center">
            <div className="w-6 h-6 bg-[#41EAD4] rounded-lg animate-pulse" />
          </div>
          <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
          <p className="text-slate-500">Signing you in...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-[#0D1B2A] rounded-xl flex items-center justify-center">
            <div className="w-6 h-6 bg-[#41EAD4] rounded-lg animate-pulse" />
          </div>
          <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  // If no stored token and no incoming token, redirect to home
  if (!user && !storedToken) {
    return <Navigate to="/" replace />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (blogsAdminOnly && !canManageBlogs()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (siteContentOnly && !canManageSiteContent()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && !isAdmin()) {
    // SEO users land on their only allowed page; everyone else → /dashboard.
    if (isSEO()) return <Navigate to="/admin/blogs" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  if (mentorOnly && !isMentor()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
